import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export type BoardNoteKindSlug = 'note' | 'entity' | 'worldelement';

export interface BoardNoteDto {
  id: string;
  boardId: string;
  kind: BoardNoteKindSlug;
  content: unknown;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BoardConnectionDto {
  id: string;
  boardId: string;
  fromNoteId: string;
  toNoteId: string;
  label?: string | null;
  style?: unknown;
}

export interface BoardDto {
  id: string;
  projectId: string;
  name: string;
  notes: BoardNoteDto[];
  connections: BoardConnectionDto[];
  updatedAt: string;
}

export interface BoardTemplateDto {
  id: string;
  name: string;
  description?: string | null;
  noteCount: number;
  connectionCount: number;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export function useBoardsForBook(projectId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<BoardDto[]>({
    enabled: !!projectId,
    queryKey: ['boards', projectId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/projects/${projectId}/boards`)
        .json<ApiEnvelope<BoardDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useCreateBoard() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId?: string; name: string }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post('api/v2/boards', {
          json: { projectId: input.projectId ?? null, name: input.name },
        })
        .json<ApiEnvelope<BoardDto>>();
      return resp.data;
    },
    onSuccess: (_, vars) => {
      if (vars.projectId) {
        void qc.invalidateQueries({ queryKey: ['boards', vars.projectId] });
      }
      void qc.invalidateQueries({ queryKey: ['boards-all'] });
    },
  });
}

export function useBoardTemplates() {
  const { getToken } = useAuth();
  return useQuery<BoardTemplateDto[]>({
    queryKey: ['board-templates'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/boards/templates')
        .json<ApiEnvelope<BoardTemplateDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useCreateBoardFromTemplate() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      templateId: string;
      projectId: string;
      name: string;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/boards/from-template/${input.templateId}`, {
          json: { projectId: input.projectId, name: input.name },
        })
        .json<ApiEnvelope<BoardDto>>();
      return resp.data;
    },
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['boards', vars.projectId] }),
  });
}

export function useAddBoardNote(bookId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      boardId: string;
      kind: BoardNoteKindSlug;
      content: unknown;
      x: number;
      y: number;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/boards/${input.boardId}/notes`, { json: input })
        .json<ApiEnvelope<BoardNoteDto>>();
      return resp.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boards', bookId] }),
  });
}

export function useUpdateBoardNote(bookId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      x: number;
      y: number;
      width?: number;
      height?: number;
      content?: unknown;
    }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/board-notes/${input.id}`, { json: input });
    },
    // Intentional: no invalidation on every drag — the canvas is the source of truth
    // mid-interaction. The list refetches when the route remounts.
    onSuccess: () => {
      // Silent success.
      void bookId;
      void qc;
    },
  });
}

export function useDeleteBoardNote(bookId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/board-notes/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boards', bookId] }),
  });
}

export interface BoardEmbeddingsDto {
  disabled: boolean;
  modelVersion?: string | null;
  notes: Array<{ noteId: string; vector: number[] }>;
}

export function useBoardEmbeddings() {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (boardId: string) => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/boards/${boardId}/embeddings`)
        .json<ApiEnvelope<BoardEmbeddingsDto>>();
      return resp.data;
    },
  });
}

export function useAddBoardConnection(bookId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      boardId: string;
      fromNoteId: string;
      toNoteId: string;
      label?: string | null;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/boards/${input.boardId}/connections`, { json: input })
        .json<ApiEnvelope<BoardConnectionDto>>();
      return resp.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boards', bookId] }),
  });
}

export function useDeleteBoardConnection(bookId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/board-connections/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boards', bookId] }),
  });
}

// --- Get single board by ID ---

export function useBoardById(boardId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<BoardDto | null>({
    enabled: !!boardId,
    queryKey: ['board-by-id', boardId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/boards/${boardId}`)
        .json<ApiEnvelope<BoardDto>>();
      return resp.data;
    },
  });
}

// --- Standalone mutation hooks (boardId-keyed invalidation) ---

export function useAddBoardNoteStandalone(boardId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      boardId: string;
      kind: BoardNoteKindSlug;
      content: unknown;
      x: number;
      y: number;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/boards/${input.boardId}/notes`, { json: input })
        .json<ApiEnvelope<BoardNoteDto>>();
      return resp.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['board-by-id', boardId] }),
  });
}

export function useUpdateBoardNoteStandalone() {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      x: number;
      y: number;
      width?: number;
      height?: number;
      content?: unknown;
    }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/board-notes/${input.id}`, { json: input });
    },
  });
}

export function useDeleteBoardNoteStandalone(boardId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/board-notes/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['board-by-id', boardId] }),
  });
}

export function useAddBoardConnectionStandalone(boardId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      boardId: string;
      fromNoteId: string;
      toNoteId: string;
      label?: string | null;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/boards/${input.boardId}/connections`, { json: input })
        .json<ApiEnvelope<BoardConnectionDto>>();
      return resp.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['board-by-id', boardId] }),
  });
}

export function useDeleteBoardConnectionStandalone(boardId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/board-connections/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['board-by-id', boardId] }),
  });
}

export function useDeleteBoard() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/boards/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['boards-all'] });
    },
  });
}

// --- Standalone listing ---

export interface BoardSummaryDto {
  id: string;
  projectId?: string | null;
  projectTitle?: string | null;
  name: string;
  notesCount: number;
  updatedAt: string;
}

export function useMyBoards() {
  const { getToken } = useAuth();
  return useQuery<BoardSummaryDto[]>({
    queryKey: ['boards-all'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/boards/mine')
        .json<ApiEnvelope<BoardSummaryDto[]>>();
      return resp.data ?? [];
    },
  });
}
